import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, ShieldCheck, KeyRound, Save, Lock } from "lucide-react";

const ROLE_LABELS = {
  admin: "Administrador", gestor: "Gestor", coordenador: "Coordenador",
  supervisor: "Supervisor", responsavel_tecnico: "Responsavel Tecnico",
  tecnico: "Tecnico", cliente: "Cliente", auditor: "Auditor",
};

export default function MyProfile() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [password, setPassword] = useState({ current: "", next: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
    }
  }, [user]);

  const handleProfileSave = async () => {
    if (!profile.name.trim()) { toast.error("Nome e obrigatorio"); return; }
    setSavingProfile(true);
    try {
      await api.put("/users/me", { name: profile.name, phone: profile.phone });
      toast.success("Perfil atualizado");
      refreshUser?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao salvar perfil");
    } finally { setSavingProfile(false); }
  };

  const handlePasswordChange = async () => {
    if (!password.current) { toast.error("Senha atual e obrigatoria"); return; }
    if (password.next.length < 6) { toast.error("Nova senha deve ter ao menos 6 caracteres"); return; }
    if (password.next !== password.confirm) { toast.error("Confirmacao nao confere"); return; }
    setSavingPassword(true);
    try {
      await api.put("/users/me/password", { current_password: password.current, new_password: password.next });
      toast.success("Senha alterada com sucesso");
      setPassword({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao alterar senha");
    } finally { setSavingPassword(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Meu Perfil</h1>
        <p className="text-sm text-muted-foreground">Gerencie suas informacoes pessoais e seguranca</p>
      </div>
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-3 border-b border-border pb-3">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-6 w-6 text-primary" />
          </div>
          <div>
            <p className="font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="font-medium text-sm">Informacoes do Perfil</h3>
          <div>
            <Label>Nome</Label>
            <Input value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input value={profile.email} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground mt-1">E-mail nao pode ser alterado por esta tela</p>
          </div>
          <div>
            <Label>Telefone</Label>
            <Input value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="(00) 0000-0000" />
          </div>
          <div className="flex items-center gap-2">
            <Label>Perfil</Label>
            <Badge variant="outline"><ShieldCheck className="h-3 w-3 mr-1" />{ROLE_LABELS[user?.role] || user?.role}</Badge>
            <Badge variant="outline"><Lock className="h-3 w-3 mr-1" />{user?.active ? "Ativo" : "Inativo"}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">Perfil, permissoes e membership sao gerenciados pelo administrador.</p>
          <Button onClick={handleProfileSave} disabled={savingProfile}><Save className="h-4 w-4 mr-1" />Salvar Alteracoes</Button>
        </div>
      </Card>
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h3 className="font-medium">Seguranca</h3>
        </div>
        <p className="text-sm text-muted-foreground">Altere sua senha. E necessario informar a senha atual.</p>
        <div className="space-y-3">
          <div>
            <Label>Senha Atual</Label>
            <Input type="password" value={password.current} onChange={(e) => setPassword((p) => ({ ...p, current: e.target.value }))} />
          </div>
          <div>
            <Label>Nova Senha</Label>
            <Input type="password" value={password.next} onChange={(e) => setPassword((p) => ({ ...p, next: e.target.value }))} />
          </div>
          <div>
            <Label>Confirmar Nova Senha</Label>
            <Input type="password" value={password.confirm} onChange={(e) => setPassword((p) => ({ ...p, confirm: e.target.value }))} />
          </div>
          <Button variant="outline" onClick={handlePasswordChange} disabled={savingPassword}>
            <KeyRound className="h-4 w-4 mr-1" />Alterar Senha
          </Button>
        </div>
      </Card>
    </div>
  );
}
